// ── Phone → Country inference ─────────────────────────────────────────────────
// Ordered longest-first so +234 matches before +2
export const DIAL_CODES = [
  { prefix:"234", country:"Nigeria",      code:"NG", flag:"🇳🇬", stateLabel:"State",           lgaLabel:"Local Government Area" },
  { prefix:"1",   country:"United States",code:"US", flag:"🇺🇸", stateLabel:"State",           lgaLabel:"County" },
  { prefix:"44",  country:"United Kingdom",code:"GB",flag:"🇬🇧", stateLabel:"Region",          lgaLabel:"Council" },
  { prefix:"27",  country:"South Africa", code:"ZA", flag:"🇿🇦", stateLabel:"Province",        lgaLabel:"Municipality" },
  { prefix:"233", country:"Ghana",        code:"GH", flag:"🇬🇭", stateLabel:"Region",          lgaLabel:"District" },
  { prefix:"254", country:"Kenya",        code:"KE", flag:"🇰🇪", stateLabel:"County",          lgaLabel:"Sub-County" },
  { prefix:"91",  country:"India",        code:"IN", flag:"🇮🇳", stateLabel:"State",           lgaLabel:"District" },
  { prefix:"49",  country:"Germany",      code:"DE", flag:"🇩🇪", stateLabel:"State (Land)",    lgaLabel:"District (Kreis)" },
  { prefix:"33",  country:"France",       code:"FR", flag:"🇫🇷", stateLabel:"Region",          lgaLabel:"Department" },
  { prefix:"55",  country:"Brazil",       code:"BR", flag:"🇧🇷", stateLabel:"State",           lgaLabel:"Municipality" },
  { prefix:"86",  country:"China",        code:"CN", flag:"🇨🇳", stateLabel:"Province",        lgaLabel:"Prefecture" },
  { prefix:"7",   country:"Russia",       code:"RU", flag:"🇷🇺", stateLabel:"Oblast",          lgaLabel:"Raion" },
  { prefix:"20",  country:"Egypt",        code:"EG", flag:"🇪🇬", stateLabel:"Governorate",     lgaLabel:"District" },
  { prefix:"212", country:"Morocco",      code:"MA", flag:"🇲🇦", stateLabel:"Region",          lgaLabel:"Province" },
  { prefix:"225", country:"Côte d'Ivoire",code:"CI", flag:"🇨🇮", stateLabel:"District",        lgaLabel:"Department" },
  { prefix:"251", country:"Ethiopia",     code:"ET", flag:"🇪🇹", stateLabel:"Region",          lgaLabel:"Zone" },
  { prefix:"255", country:"Tanzania",     code:"TZ", flag:"🇹🇿", stateLabel:"Region",          lgaLabel:"District" },
  { prefix:"256", country:"Uganda",       code:"UG", flag:"🇺🇬", stateLabel:"Region",          lgaLabel:"District" },
];

export function inferCountryFromPhone(raw) {
  // Strip everything except digits and leading +
  const digits = raw.replace(/[^\d]/g,"");
  // Try matching longest prefix first (already ordered longest first per country)
  // Sort by prefix length desc to match most specific first
  const sorted = [...DIAL_CODES].sort((a,b)=>b.prefix.length-a.prefix.length);
  for (const d of sorted) {
    if (digits.startsWith(d.prefix)) return d;
  }
  return null;
}

// Nigeria geo data
import { NG_LGAS_COMPLETE } from "../data/nigeria-lgas.js";

export const NG_STATES = [
  "Abia","Adamawa","Akwa Ibom","Anambra","Bauchi","Bayelsa","Benue","Borno",
  "Cross River","Delta","Ebonyi","Edo","Ekiti","Enugu","FCT – Abuja",
  "Gombe","Imo","Jigawa","Kaduna","Kano","Katsina","Kebbi","Kogi","Kwara",
  "Lagos","Nasarawa","Niger","Ogun","Ondo","Osun","Oyo","Plateau","Rivers",
  "Sokoto","Taraba","Yobe","Zamfara",
];

export function getStateLgas(state) {
  return NG_LGAS_COMPLETE[state] || [];
}

// US states
export const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio",
  "Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
  "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming","District of Columbia",
];

// Generic fallback
export const GENERIC_REGIONS = ["Region 1","Region 2","Region 3","Region 4","Region 5"];
export const GENERIC_LGAS = ["Area 1","Area 2","Area 3","Area 4","Area 5"];

export function getStatesForCountry(code) {
  if (code==="NG") return NG_STATES;
  if (code==="US") return US_STATES;
  return GENERIC_REGIONS;
}

export function getLgasForState(code, state) {
  if (code==="NG") return getStateLgas(state);
  return GENERIC_LGAS;
}

// ── Persona filtering ──────────────────────────────────────────────────────────
//
// v1.28.3: `isPersonaInVoterGeo` is a single-persona predicate extracted from
// `filterPersonasForVoter` so that direct-slug navigation paths (a voter
// hitting /nddc for an agency, /tinubu for an official — any route that
// bypasses the geo-filtered list) can correctly set `persona.outOfGeo`
// instead of blindly flagging it as out-of-scope just because the fetched
// personas list (type=official by default) happened not to contain it.
//
// The predicate reads both camelCase (frontend-normalised) and snake_case
// (raw from /api/candidates/by-slug/:slug) so callers don't have to
// pre-map the persona shape.
export function isPersonaInVoterGeo(persona, voter) {
  if (!persona) return false;
  // No voter (anonymous browse) or no country picked yet → lean generous;
  // the backend still enforces its own geo rules before anything sensitive.
  if (!voter || !voter.countryCode) return true;

  const geoCountry = persona.geoCountry ?? persona.geo_country ?? "";
  const geoState = persona.geoState ?? persona.geo_state ?? "";
  const geoLga = persona.geoLga ?? persona.geo_lga ?? "";

  // Persona has no country scope → visible to everyone (e.g. a generic
  // global agency). Mirrors the backend's `geo_country IS NULL` branch.
  if (!geoCountry) return true;
  // Must match country (ISO code comparison, case-insensitive on both sides
  // to survive any historical mixed casing in DB).
  if (geoCountry.toUpperCase() !== String(voter.countryCode).toUpperCase()) return false;
  // State is only a constraint if BOTH sides have a value. A persona with
  // no state scope (NDDC-style nationwide agency) matches every voter in
  // the country. A voter with no state picked matches every persona in
  // the country — the onboarding flow currently requires a state, but
  // we keep this lenient for legacy voters who slipped through.
  if (geoState && voter.state && geoState !== voter.state) return false;
  // Same logic for LGA.
  if (geoLga && voter.lga && geoLga !== voter.lga) return false;
  return true;
}

export function filterPersonasForVoter(personas, voter) {
  if (!voter || !voter.countryCode) return personas;
  return personas.filter(p => isPersonaInVoterGeo(p, voter));
}
